import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api, { errorMessage } from "../lib/api";
import { hasPermission } from "../lib/permissions";
import { STATUS_FILTER_OPTIONS, STATUS_LABELS, managerFilterParams, mobileRowSummary } from "../lib/collaborators";
import CollaboratorPicker from "../components/CollaboratorPicker";
import useCollabReferentials from "../hooks/useCollabReferentials";
import useSkillsReferentials from "../hooks/useSkillsReferentials";
import { LEVEL_CODES, skillFilterParams } from "../lib/skills";
import { StatusBadge, Initials } from "../components/CollaboratorBits";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Badge } from "../components/ui/badge";
import { ResponsivePopover as Popover, ResponsivePopoverContent as PopoverContent, ResponsivePopoverTrigger as PopoverTrigger } from "../components/ui/responsive-popover";
import { MobileSheet, MobileSheetBody, MobileSheetClose, MobileSheetCloseButton, MobileSheetContent, MobileSheetHeader, MobileSheetTitle, MobileSheetTrigger } from "../components/ui/mobile-sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { GraduationCap, Loader2, MoreHorizontal, Plus, Search, SlidersHorizontal, X, Network, UsersRound, AlertCircle, Upload } from "lucide-react";
import { toast } from "sonner";

const PAGE_SIZE = 25;

export default function Collaborators() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const refs = useCollabReferentials();
  const skillRefs = useSkillsReferentials();
  const canCreate = hasPermission(user, "collaborators:create");
  const isAdmin = user?.role === "admin";
  // The manager relation is detail-level data: the server refuses the filter to whoever cannot read it, so the control is not offered to them.
  const canFilterByManager = isAdmin || hasPermission(user, "collaborators:view_details");

  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [status, setStatus] = useState("all");
  const [relationType, setRelationType] = useState("all");
  const [serviceId, setServiceId] = useState("all");
  const [zoneId, setZoneId] = useState("all");
  // Arriving from « Équipe directe » of a fiche (router state): the directory opens already filtered on that manager.
  const fromTeam = useLocation().state?.manager || null;
  const [manager, setManager] = useState(fromTeam ? { id: fromTeam.id, kind: "collaborator", label: fromTeam.label } : null);
  const [includeArchived, setIncludeArchived] = useState(false);
  // « qui sait faire / qui est habilité à faire quoi » : niveau et date n'ont de sens qu'avec une compétence
  const [jobFunctionId, setJobFunctionId] = useState("");
  const [skillId, setSkillId] = useState("");
  const [minLevel, setMinLevel] = useState("");
  const [accreditedOn, setAccreditedOn] = useState("");
  const [items, setItems] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const params = useMemo(() => {
    const p = { limit: PAGE_SIZE };
    if (debounced) p.search = debounced;
    if (status !== "all") p.status = status;
    if (relationType !== "all") p.relation_type = relationType;
    if (serviceId !== "all") p.service_id = serviceId;
    if (zoneId !== "all") p.zone_id = zoneId;
    if (includeArchived) p.include_archived = true;
    return { ...p, ...managerFilterParams(manager), ...skillFilterParams({ jobFunctionId, skillId, minLevel, accreditedOn }) };
  }, [debounced, status, relationType, serviceId, zoneId, manager, includeArchived, jobFunctionId, skillId, minLevel, accreditedOn]);

  const load = useCallback(async () => {
    setLoadError(false);
    setItems(null);
    try {
      const { data } = await api.get("/collaborators", { params });
      setItems(data.items);
      setCursor(data.next_cursor || null);
      setHasMore(!!data.has_more);
    } catch (e) {
      setLoadError(true);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const { data } = await api.get("/collaborators", { params: { ...params, cursor } });
      setItems((prev) => [...prev, ...data.items]);
      setCursor(data.next_cursor || null);
      setHasMore(!!data.has_more);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setLoadingMore(false);
    }
  };

  const skillFilterCount = [jobFunctionId, skillId].filter(Boolean).length;
  const activeFilters = status !== "all" || relationType !== "all" || serviceId !== "all" || zoneId !== "all" || !!manager || includeArchived || skillFilterCount > 0;
  const clearSkill = () => {
    setSkillId("");
    setMinLevel("");
    setAccreditedOn("");
  };
  const reset = () => {
    setStatus("all");
    setRelationType("all");
    setServiceId("all");
    setZoneId("all");
    setManager(null);
    setIncludeArchived(false);
    setJobFunctionId("");
    clearSkill();
  };

  const chips = [];
  if (status !== "all") chips.push({ id: "status", label: STATUS_LABELS[status], onRemove: () => setStatus("all") });
  if (relationType !== "all") chips.push({ id: "type", label: refs.relationLabel(relationType), onRemove: () => setRelationType("all") });
  if (serviceId !== "all") chips.push({ id: "service", label: refs.serviceName(serviceId), onRemove: () => setServiceId("all") });
  if (zoneId !== "all") chips.push({ id: "zone", label: `Zone : ${refs.zoneLabel(zoneId)}`, onRemove: () => setZoneId("all") });
  if (manager) chips.push({ id: "manager", label: `Équipe de ${manager.label}`, onRemove: () => setManager(null) });
  if (includeArchived) chips.push({ id: "archived", label: "Avec les archivées", onRemove: () => setIncludeArchived(false) });
  if (jobFunctionId) chips.push({ id: "function", label: skillRefs.functionLabel(jobFunctionId), onRemove: () => setJobFunctionId(""), desktop: true });
  if (skillId) {
    const level = minLevel ? ` ≥ ${skillRefs.levelLabel(minLevel)}` : "";
    const on = accreditedOn ? `, habilité le ${accreditedOn.split("-").reverse().join("/")}` : "";
    chips.push({ id: "skill", label: `${skillRefs.skillLabel(skillId)}${level}${on}`, onRemove: clearSkill, desktop: true });
  }

  const filterControls = (suffix) => (
    <>
      <Select value={status} onValueChange={setStatus}>
        <SelectTrigger className="w-full md:w-40" data-testid={`filter-status${suffix}`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tous les statuts</SelectItem>
          {STATUS_FILTER_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={relationType} onValueChange={setRelationType}>
        <SelectTrigger className="w-full md:w-44" data-testid={`filter-type${suffix}`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tous les types</SelectItem>
          {refs.relation_types.map((r) => <SelectItem key={r.code} value={r.code}>{r.label}{r.active ? "" : " (inactif)"}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={serviceId} onValueChange={setServiceId}>
        <SelectTrigger className="w-full md:w-44" data-testid={`filter-service${suffix}`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tous les services</SelectItem>
          {refs.services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.active ? "" : " (inactif)"}</SelectItem>)}
        </SelectContent>
      </Select>
      {canFilterByManager && (
        <div className="w-full md:w-52">
          <CollaboratorPicker testid={`filter-manager${suffix}`} value={manager?.id} label={manager ? `Équipe de ${manager.label}` : ""} placeholder="Tous les responsables" allowAccounts
            onChange={(id, name, kind) => setManager(id ? { id, kind: kind || "collaborator", label: name } : null)} />
        </div>
      )}
      {refs.zones.length > 0 && (
        <Select value={zoneId} onValueChange={setZoneId}>
          <SelectTrigger className="w-full md:w-40" data-testid={`filter-zone${suffix}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les zones</SelectItem>
            {refs.zones.map((z) => <SelectItem key={z.code} value={z.code}>{z.label}{z.active ? "" : " (inactive)"}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
    </>
  );

  const ANY = "__any__";
  const skillControls = (suffix) => (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label className="text-xs text-slate-500">Fonction métier</Label>
        <Select value={jobFunctionId || ANY} onValueChange={(v) => setJobFunctionId(v === ANY ? "" : v)}>
          <SelectTrigger data-testid={`filter-function${suffix}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Toutes les fonctions</SelectItem>
            {skillRefs.functions.map((f) => <SelectItem key={f.id} value={f.id}>{skillRefs.functionLabel(f.id)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs text-slate-500">Compétence</Label>
        <Select value={skillId || ANY} onValueChange={(v) => (v === ANY ? clearSkill() : setSkillId(v))}>
          <SelectTrigger data-testid={`filter-skill${suffix}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Toutes les compétences</SelectItem>
            {skillRefs.skills.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}{s.active ? "" : " (inactive)"}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {skillId && (
        <>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-500">Niveau minimal</Label>
            <Select value={minLevel || ANY} onValueChange={(v) => setMinLevel(v === ANY ? "" : v)}>
              <SelectTrigger data-testid={`filter-level${suffix}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Tous les niveaux</SelectItem>
                {LEVEL_CODES.map((code) => <SelectItem key={code} value={code}>{skillRefs.levelLabel(code)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`filter-accredited${suffix}`} className="text-xs text-slate-500">Habilité(e) à la date du</Label>
            <Input id={`filter-accredited${suffix}`} type="date" className="text-base md:text-sm" value={accreditedOn} onChange={(e) => setAccreditedOn(e.target.value)}
              data-testid={`filter-accredited${suffix}`} />
            <p className="text-xs text-slate-500">{accreditedOn ? "Habilitation valable ce jour-là, non révoquée." : "Vide : niveau de compétence enregistré sur la fiche."}</p>
          </div>
        </>
      )}
    </div>
  );

  const rowSummary = (c) => mobileRowSummary(c, refs.serviceName);

  return (
    <div className="space-y-4" data-testid="collaborators-page">
      <div className="flex items-start justify-between gap-3 md:flex-wrap">
        <div className="min-w-0 flex-1 md:flex-none">
          <h1 className="font-display font-bold text-2xl text-slate-900">Collaborateurs</h1>
          <p className="text-sm text-slate-500">Les personnes qui travaillent avec Jamora, avec ou sans compte CRM.</p>
        </div>
        <div className="hidden md:flex gap-2">
          <Button variant="outline" onClick={() => navigate("/collaborateurs/organisation")} data-testid="btn-collab-organisation" className="px-3 md:px-4">
            <Network size={16} className="md:mr-1.5" /> <span className="hidden md:inline">Organisation</span>
            <span className="sr-only md:hidden">Organisation</span>
          </Button>
          {canCreate && (
            <Button variant="outline" onClick={() => navigate("/collaborateurs/import")} data-testid="btn-collab-import" className="px-3 md:px-4">
              <Upload size={16} className="md:mr-1.5" /> <span className="hidden md:inline">Importer</span>
              <span className="sr-only md:hidden">Importer des collaborateurs</span>
            </Button>
          )}
          {canCreate && (
            <Button onClick={() => navigate("/collaborateurs/nouveau")} data-testid="btn-collab-new" className="px-3 md:px-4">
              <Plus size={16} className="md:mr-1.5" /> <span className="hidden md:inline">Nouveau collaborateur</span>
              <span className="sr-only md:hidden">Nouveau collaborateur</span>
            </Button>
          )}
        </div>
        <MobileSheet>
          <MobileSheetTrigger asChild>
            <Button variant="outline" size="icon" className="md:hidden shrink-0" aria-label="Actions collaborateurs" data-testid="btn-collab-actions">
              <MoreHorizontal size={20} />
            </Button>
          </MobileSheetTrigger>
          <MobileSheetContent className="md:hidden" aria-describedby={undefined}>
            <MobileSheetHeader>
              <MobileSheetTitle>Actions collaborateurs</MobileSheetTitle>
              <MobileSheetCloseButton />
            </MobileSheetHeader>
            <MobileSheetBody>
              <div className="space-y-2">
                <MobileSheetClose asChild>
                  <Button variant="outline" className="w-full justify-start gap-3" onClick={() => navigate("/collaborateurs/organisation")} data-testid="btn-collab-organisation-mobile">
                    <Network size={18} /> Organisation
                  </Button>
                </MobileSheetClose>
                {canCreate && (
                  <>
                    <MobileSheetClose asChild>
                      <Button variant="outline" className="w-full justify-start gap-3" onClick={() => navigate("/collaborateurs/import")} data-testid="btn-collab-import-mobile">
                        <Upload size={18} /> Importer des collaborateurs
                      </Button>
                    </MobileSheetClose>
                    <MobileSheetClose asChild>
                      <Button className="w-full justify-start gap-3" onClick={() => navigate("/collaborateurs/nouveau")} data-testid="btn-collab-new-mobile">
                        <Plus size={18} /> Nouveau collaborateur
                      </Button>
                    </MobileSheetClose>
                  </>
                )}
              </div>
            </MobileSheetBody>
          </MobileSheetContent>
        </MobileSheet>
      </div>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-0 md:min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <Input
              data-testid="input-collab-search" aria-label="Rechercher un collaborateur"
              placeholder="Rechercher par nom, référence ou fonction" value={search} onChange={(e) => setSearch(e.target.value)}
              className="pl-9 text-base md:text-sm"
            />
          </div>
          <div className="hidden md:flex flex-wrap gap-3 items-center">
            {filterControls("")}
            {skillRefs.status === "ready" && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="shrink-0" data-testid="btn-collab-skill-filters">
                    <GraduationCap size={16} className="mr-1.5" /> Compétences
                    {skillFilterCount > 0 && <span className="ml-1.5 rounded-full bg-jamora text-white text-2xs px-1.5">{skillFilterCount}</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-80">{skillControls("")}</PopoverContent>
              </Popover>
            )}
            <div className="flex items-center gap-2">
              <Checkbox id="include-archived" checked={includeArchived} onCheckedChange={(v) => setIncludeArchived(v === true)} data-testid="filter-archived" />
              <Label htmlFor="include-archived" className="font-normal text-sm whitespace-nowrap">Avec les archivées</Label>
            </div>
            {activeFilters && (
              <Button variant="ghost" size="sm" onClick={reset} className="text-slate-600 shrink-0" data-testid="btn-collab-reset">
                <X size={14} className="mr-1" /> Réinitialiser
              </Button>
            )}
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="icon" className="md:hidden shrink-0 h-10 w-10" data-testid="btn-collab-filters-toggle" aria-label="Filtres">
                <SlidersHorizontal size={16} />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[min(20rem,calc(100vw-2rem))] space-y-3 md:hidden">
              <div className="text-sm font-semibold text-slate-900">Filtres</div>
              <div className="space-y-2">{filterControls("-mobile")}</div>
              {skillRefs.status === "ready" && <div className="border-t border-slate-100 pt-3">{skillControls("-mobile")}</div>}
              <div className="flex items-center gap-2">
                <Checkbox id="include-archived-m" checked={includeArchived} onCheckedChange={(v) => setIncludeArchived(v === true)} data-testid="filter-archived-mobile" />
                <Label htmlFor="include-archived-m" className="font-normal text-sm">Avec les archivées</Label>
              </div>
              <Button variant="outline" size="sm" className="w-full" onClick={reset} disabled={!activeFilters} data-testid="btn-collab-reset-mobile">Réinitialiser</Button>
            </PopoverContent>
          </Popover>
        </div>
        {chips.length > 0 && (
          // on desktop the other filters are visible controls; only the ones folded into « Compétences » need a chip there
          <div className={`flex flex-wrap gap-1.5 ${chips.some((c) => c.desktop) ? "" : "md:hidden"}`}>
            {chips.map((chip) => (
              <button key={chip.id} type="button" onClick={chip.onRemove} data-testid={`chip-${chip.id}`}
                className={`${chip.desktop ? "inline-flex" : "inline-flex md:hidden"} items-center gap-1 text-xs bg-slate-100 text-slate-700 rounded-full pl-2.5 pr-1.5 py-1 hover:bg-slate-200`}>
                {chip.label} <X size={12} />
              </button>
            ))}
          </div>
        )}
      </div>

      {loadError && (
        <Card className="p-8 text-center space-y-3" data-testid="collab-load-error">
          <AlertCircle className="mx-auto text-slate-400" />
          <p className="text-sm text-slate-600">Impossible de charger les collaborateurs. Réessayez dans quelques instants.</p>
          <Button variant="outline" onClick={load} data-testid="btn-collab-retry">Réessayer</Button>
        </Card>
      )}

      {!loadError && items === null && (
        <div className="space-y-2" data-testid="collab-loading">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-14 w-full" />)}
        </div>
      )}

      {!loadError && items !== null && items.length === 0 && (
        <Card className="p-10 text-center space-y-3" data-testid="collab-empty">
          <UsersRound className="mx-auto text-slate-300" size={32} />
          {debounced || activeFilters ? (
            <>
              <p className="text-sm text-slate-600">Aucun collaborateur ne correspond à ces critères.</p>
              <Button variant="outline" size="sm" onClick={() => { setSearch(""); reset(); }} data-testid="btn-collab-reset-empty">Réinitialiser les filtres</Button>
            </>
          ) : (
            <>
              <p className="font-medium text-slate-900">Aucun collaborateur pour le moment</p>
              <p className="text-sm text-slate-500 max-w-md mx-auto">
                {canCreate ? "Ajoutez la première personne pour constituer l'annuaire de l'équipe." : "Vous ne voyez ici que votre propre fiche et, selon vos droits, celle de votre équipe."}
              </p>
              {canCreate && (
                <Button onClick={() => navigate("/collaborateurs/nouveau")}>
                  <Plus size={16} className="mr-1.5" /> Nouveau collaborateur
                </Button>
              )}
            </>
          )}
        </Card>
      )}

      {!loadError && items !== null && items.length > 0 && (
        <>
          <Card className="hidden md:block p-0 overflow-hidden" data-testid="collab-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-28">Référence</TableHead>
                  <TableHead>Nom</TableHead>
                  <TableHead>Type de relation</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Statut</TableHead>
                  {isAdmin && <TableHead>Compte CRM</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((c) => (
                  <TableRow key={c.id} data-testid={`collab-row-${c.id}`}>
                    <TableCell className="text-slate-500 text-xs">{c.reference}</TableCell>
                    <TableCell>
                      <Link to={`/collaborateurs/${c.id}`} className="inline-flex items-center gap-2.5 font-medium text-slate-900 hover:text-jamora" data-testid={`collab-link-${c.id}`}>
                        <Initials name={c.display_name} />
                        <span className="min-w-0">
                          <span className="block">{c.display_name}{c.is_me && <Badge variant="outline" className="ml-1.5 text-2xs">Moi</Badge>}</span>
                          {c.job_title && <span className="block text-xs font-normal text-slate-500">{c.job_title}</span>}
                        </span>
                      </Link>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{refs.relationLabel(c.relation_type)}</TableCell>
                    <TableCell className="text-sm text-slate-600">{refs.serviceName(c.service_id) || "Aucun service"}</TableCell>
                    <TableCell><StatusBadge status={c.status} archived={c.archived} /></TableCell>
                    {isAdmin && <TableCell className="text-sm text-slate-600">{c.user_id ? "Rattaché" : "Sans compte"}</TableCell>}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          <div className="md:hidden space-y-2" data-testid="collab-cards">
            {items.map((c) => (
              <Link key={c.id} to={`/collaborateurs/${c.id}`} data-testid={`collab-card-${c.id}`}
                className="flex items-start gap-3 bg-white border border-slate-200 rounded-xl p-3.5 min-h-[56px] active:bg-slate-50">
                <Initials name={c.display_name} className="w-9 h-9" />
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="font-medium text-slate-900 break-words">{c.display_name}{c.is_me && <span className="ml-1.5 text-2xs text-slate-500">(moi)</span>}</div>
                  {rowSummary(c) && <div className="text-xs text-slate-500 truncate">{rowSummary(c)}</div>}
                </div>
                <div className="shrink-0 pt-0.5">
                  <StatusBadge status={c.status} archived={c.archived} />
                </div>
              </Link>
            ))}
          </div>

          {hasMore && (
            <div className="flex justify-center pt-1">
              <Button variant="outline" onClick={loadMore} disabled={loadingMore} data-testid="btn-collab-more">
                {loadingMore ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null} Afficher plus
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
